<template>
  <div class="referrer-detail-page">
    <div class="page-header">
      <button @click="goBack" class="back-btn">← 返回列表</button>
      <h2 class="page-title">跑手推广人详情</h2>
      <div class="header-actions">
        <button @click="openEditModal" class="action-btn edit">编辑</button>
        <button @click="handleDelete" class="action-btn delete-btn">删除</button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">加载中...</div>

    <template v-else-if="detail">
      <!-- 基本信息 -->
      <div class="section-card">
        <div class="section-title">基本信息</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">ID</span>
            <span class="info-value">{{ detail.id }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">姓名</span>
            <span class="info-value">
              {{ detail.user?.real_name || detail.user?.nickname || detail.user?.name || '-' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">手机号</span>
            <span class="info-value">{{ detail.user?.phone || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">认证状态</span>
            <span :class="'status-tag ' + certStatusClass(detail.cert_status)">
              {{ certStatusText(detail.cert_status) }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">认证备注</span>
            <span class="info-value">{{ detail.cert_remark || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">状态</span>
            <span :class="'status-tag ' + (detail.status === 1 ? 'enabled' : 'disabled')">
              {{ detail.status === 1 ? '启用' : '禁用' }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">开始日期</span>
            <span class="info-value">{{ formatDate(detail.start_date) || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">结束日期</span>
            <span class="info-value">{{ formatDate(detail.end_date) || '长期有效' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">创建时间</span>
            <span class="info-value">{{ formatDateTime(detail.created_at) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">更新时间</span>
            <span class="info-value">{{ formatDateTime(detail.updated_at) }}</span>
          </div>
        </div>
      </div>

      <!-- 关联跑手 -->
      <div class="section-card">
        <div class="section-title">
          关联跑手（即推广总人数）
          <span class="count-badge">{{ runners.length }}</span>
          <button @click="openRunnerModal" class="action-btn add-runner-btn">添加关联跑手</button>
        </div>
        <div class="table-container">
          <div v-if="runnersLoading" class="loading-state">加载中...</div>
          <table v-else class="runner-table">
            <thead>
              <tr>
                <th>关联表ID</th>
                <th>跑手</th>
                <th>手机号</th>
                <th>开始日期</th>
                <th>结束日期</th>
                <th>状态</th>
                <th>创建时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in runners" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.runner?.real_name || item.runner?.name || '-' }}</td>
                <td>{{ item.runner?.phone || '-' }}</td>
                <td>{{ formatDate(item.start_date) || '-' }}</td>
                <td>{{ formatDate(item.end_date) || '长期有效' }}</td>
                <td>
                  <span :class="'status-tag ' + (item.status === 1 ? 'enabled' : 'disabled')">
                    {{ item.status === 1 ? '启用' : '禁用' }}
                  </span>
                </td>
                <td>{{ formatDateTime(item.created_at) }}</td>
              </tr>
              <tr v-if="runners.length === 0">
                <td colspan="7" class="empty-row">暂无关联跑手</td>
              </tr>
            </tbody>
            </table>
            </div>
            </div>

            <!-- 钱包信息 -->
            <div class="section-card">
            <div class="section-title">钱包信息</div>
            <div v-if="walletLoading" class="loading-state">加载中...</div>
            <div v-else-if="wallet" class="info-grid">
            <div class="info-item">
              <span class="info-label">账户状态</span>
              <span :class="'wallet-status-tag ' + walletStatusClass(wallet.status)">
                {{ walletStatusText(wallet.status) }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">余额</span>
              <span class="info-value amount">{{ (wallet.balance || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">冻结余额</span>
              <span class="info-value amount">{{ (wallet.freezeBalance || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">累计服务费</span>
              <span class="info-value amount">{{ (wallet.totalCommission || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">累计提现</span>
              <span class="info-value amount">{{ (wallet.totalWithdraw || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">累计消费</span>
              <span class="info-value amount">{{ (wallet.totalConsume || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">累计退款</span>
              <span class="info-value amount">{{ (wallet.totalRefund || 0) / 100 }} 元</span>
            </div>
            <div class="info-item">
              <span class="info-label">风险等级</span>
              <span class="info-value">{{ wallet.riskLevel || 0 }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">备注</span>
              <span class="info-value">{{ wallet.remark || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">最后结算</span>
              <span class="info-value">{{ formatDateTime(wallet.lastSettleTime) }}</span>
            </div>
            </div>
            <div v-else class="loading-state">暂无钱包信息</div>
            </div>

            <!-- 钱包流水 -->
            <div class="section-card">
            <div class="section-title">钱包流水</div>
            <div class="tx-filter-bar">
            <select v-model="txType" class="filter-select" @change="searchTransactions">
              <option value="">全部类型</option>
              <option value="1">充值</option>
              <option value="2">消费</option>
              <option value="3">提现</option>
              <option value="4">退款</option>
              <option value="5">冻结</option>
              <option value="6">解冻</option>
              <option value="7">服务费</option>
            </select>
            </div>
            <div class="table-container">
            <div v-if="transactionsLoading" class="loading-state">加载中...</div>
            <table v-else class="tx-table">
              <thead>
                <tr>
                  <th>流水ID</th>
                  <th>类型</th>
                  <th>金额</th>
                  <th>描述</th>
                  <th>状态</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="tx in transactions" :key="tx.id">
                  <td>{{ tx.id }}</td>
                  <td>{{ txTypeText(tx.transaction_type) }}</td>
                  <td :class="'amount ' + (tx.amount >= 0 ? 'plus' : 'minus')">
                    {{ (tx.amount || 0) / 100 }} 元
                  </td>
                  <td>{{ tx.description || '-' }}</td>
                  <td>{{ txStatusText(tx.status) }}</td>
                  <td>{{ formatDateTime(tx.created_at) }}</td>
                </tr>
                <tr v-if="transactions.length === 0">
                  <td colspan="6" class="empty-row">暂无流水</td>
                </tr>
              </tbody>
            </table>
            </div>
            <div class="pagination">
            <button :disabled="txCurrentPage === 1 || transactionsLoading" @click="changeTxPage(txCurrentPage - 1)" class="page-btn">上一页</button>
            <span class="page-info">第 {{ txCurrentPage }} / {{ txTotalPages }} 页</span>
            <button :disabled="txCurrentPage === txTotalPages || transactionsLoading" @click="changeTxPage(txCurrentPage + 1)" class="page-btn">下一页</button>
            </div>
            </div>
            </template>

    <div v-else class="loading-state">未找到该跑手推广人</div>

    <!-- 编辑弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>编辑跑手推广人</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <form @submit.prevent="submitForm" class="modal-form">
          <div class="form-item">
            <label>认证状态</label>
            <select v-model="form.certStatus" class="form-select">
              <option :value="0">待邀约</option>
              <option :value="1">已邀约待面谈</option>
              <option :value="2">已面谈待核验原件</option>
              <option :value="3">已核验证件原件待提交照片</option>
              <option :value="4">已提交证件照片待核验照片</option>
              <option :value="5">已核验证件照片待签约</option>
              <option :value="6">已签约</option>
              <option :value="7">已拒绝</option>
            </select>
          </div>
          <div class="form-item">
            <label>认证备注</label>
            <textarea v-model="form.certRemark" class="form-textarea" placeholder="选填，填写认证备注"></textarea>
          </div>
          <div class="form-item">
            <label>开始日期</label>
            <input type="date" v-model="form.startDate" class="form-input" />
          </div>
          <div class="form-item">
            <label>结束日期</label>
            <input type="date" v-model="form.endDate" class="form-input" />
            <span class="form-hint">留空表示长期有效</span>
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model="form.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="modal-footer">
            <button type="button" @click="closeModal" class="btn-cancel">取消</button>
            <button type="submit" class="btn-submit">保存修改</button>
          </div>
        </form>
      </div>
    </div>

    <!-- 添加关联跑手弹窗 -->
    <div v-if="showRunnerModal" class="modal-overlay" @click.self="closeRunnerModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>添加关联跑手</h3>
          <button @click="closeRunnerModal" class="close-btn">×</button>
        </div>
        <form @submit.prevent="submitRunnerForm" class="modal-form">
          <div class="form-item">
            <label>搜索跑手</label>
            <input
              v-model="runnerKeyword"
              type="text"
              class="form-input"
              placeholder="输入手机号或姓名搜索"
              @input="searchRunners"
            />
          </div>
          <div class="form-item">
            <label>选择跑手</label>
            <select v-model="runnerForm.runnerId" class="form-select" :disabled="runnerSearching">
              <option :value="null">请选择跑手</option>
              <option
                v-for="opt in runnerOptions"
                :key="opt.id"
                :value="opt.id"
              >
                {{ opt.user?.real_name || opt.user?.name || '暂无可添加的跑手' }}{{ opt.user?.phone ? '（' + opt.user.phone + '）' : '' }}
              </option>
            </select>
          </div>
          <div class="form-item">
            <label>开始日期</label>
            <input type="date" v-model="runnerForm.startDate" class="form-input" />
          </div>
          <div class="form-item">
            <label>结束日期</label>
            <input type="date" v-model="runnerForm.endDate" class="form-input" />
            <span class="form-hint">留空表示长期有效</span>
          </div>
          <div class="form-item">
            <label>状态</label>
            <select v-model="runnerForm.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>
          <div class="modal-footer">
            <button type="button" @click="closeRunnerModal" class="btn-cancel">取消</button>
            <button type="submit" class="btn-submit">确认关联</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getRunnerReferrerDetail,
  updateRunnerReferrer,
  deleteRunnerReferrer
} from '@/api/runner-referrers'
import {
  getRunnerReferrerRunnerList,
  createRunnerReferrerRunner
} from '@/api/runner-referrer-runner'
import { getRunnerList } from '@/api/runners'
import {
  getRunnerReferrerWalletInfo,
  getRunnerReferrerWalletTransactions,
  updateRunnerReferrerWallet
} from '@/api/runner-referrer-wallet'

export default {
  name: 'RunnerReferrerDetail',
  data() {
    return {
      id: null,
      loading: false,
      detail: null,
      runners: [],
      runnersLoading: false,
      wallet: null,
      walletLoading: false,
      transactions: [],
      transactionsLoading: false,
      txCurrentPage: 1,
      txPageSize: 10,
      txTotalPages: 1,
      txType: '',
      showModal: false,
      showRunnerModal: false,
      runnerForm: {
        runnerId: null,
        startDate: '',
        endDate: '',
        status: 1
      },
      runnerOptions: [],
      runnerKeyword: '',
      runnerSearching: false,
      form: {
        startDate: '',
        endDate: '',
        status: 1,
        certStatus: 0,
        certRemark: ''
      }
    }
  },
  created() {
    this.id = this.$route.params.id
    this.loadDetail()
    this.loadRunners()
    this.loadWallet()
    this.loadTransactions()
  },
  methods: {
    async loadDetail() {
      try {
        this.loading = true
        const response = await getRunnerReferrerDetail(this.id)
        if (response && response.code === 0 && response.data) {
          this.detail = response.data
        } else {
          this.detail = null
          alert(response?.message || '加载详情失败')
        }
      } catch (error) {
        alert('加载详情失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    async loadRunners() {
      try {
        this.runnersLoading = true
        const response = await getRunnerReferrerRunnerList({
          runner_referrer_id: this.id,
          page: 1,
          pageSize: 20
        })
        if (response && response.code === 0 && response.data) {
          this.runners = response.data.list || response.data.data || []
        }
      } catch (error) {
        // 关联跑手加载失败不阻塞主流程
      } finally {
        this.runnersLoading = false
      }
    },
    async loadWallet() {
      try {
        this.walletLoading = true
        const response = await getRunnerReferrerWalletInfo(this.id)
        if (response && response.code === 0 && response.data) {
          this.wallet = response.data
        }
      } catch (error) {
        // 钱包信息加载失败不阻塞主流程
      } finally {
        this.walletLoading = false
      }
    },
    async loadTransactions() {
      try {
        this.transactionsLoading = true
        const params = {
          page: this.txCurrentPage,
          pageSize: this.txPageSize
        }
        if (this.txType) params.type = this.txType
        const response = await getRunnerReferrerWalletTransactions(this.id, params)
        if (response && response.code === 0 && response.data) {
          const d = response.data
          this.transactions = d.list || d.data || []
          this.txTotalPages = d.total
            ? Math.ceil(d.total / this.txPageSize)
            : (d.lastPage || 1)
        }
      } catch (error) {
        // 流水加载失败不阻塞主流程
      } finally {
        this.transactionsLoading = false
      }
    },
    changeTxPage(page) {
      if (page >= 1 && page <= this.txTotalPages) {
        this.txCurrentPage = page
        this.loadTransactions()
      }
    },
    searchTransactions() {
      this.txCurrentPage = 1
      this.loadTransactions()
    },
    goBack() {
      this.$router.push({ path: '/runner-referrer/referrers' })
    },
    openEditModal() {
      if (!this.detail) return
      this.form = {
        startDate: this.formatDate(this.detail.start_date) || '',
        endDate: this.formatDate(this.detail.end_date) || '',
        status: this.detail.status,
        certStatus: this.detail.cert_status ?? 0,
        certRemark: this.detail.cert_remark || ''
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    openRunnerModal() {
      this.runnerForm = {
        runnerId: null,
        startDate: '',
        endDate: '',
        status: 1
      }
      this.runnerOptions = []
      this.runnerKeyword = ''
      this.showRunnerModal = true
      this.searchRunners()
    },
    closeRunnerModal() {
      this.showRunnerModal = false
    },
    async searchRunners() {
      try {
        this.runnerSearching = true
        const response = await getRunnerList({ keyword: this.runnerKeyword, limit: 50 })
        if (response && response.code === 0 && response.data) {
          this.runnerOptions = response.data.list || []
        } else {
          this.runnerOptions = []
        }
      } catch (error) {
        this.runnerOptions = []
      } finally {
        this.runnerSearching = false
      }
    },
    async submitRunnerForm() {
      if (!this.runnerForm.runnerId) {
        alert('请选择要关联的跑手')
        return
      }
      try {
        const data = {
          runner_referrer_id: this.id,
          runner_id: this.runnerForm.runnerId,
          start_date: this.runnerForm.startDate || null,
          end_date: this.runnerForm.endDate || null,
          status: this.runnerForm.status
        }
        const response = await createRunnerReferrerRunner(data)
        if (response && response.code === 0) {
          alert('关联成功')
          this.closeRunnerModal()
          this.loadRunners()
        } else {
          alert(response?.message || '关联失败')
        }
      } catch (error) {
        alert('操作失败，请稍后重试')
      }
    },
    async submitForm() {
      try {
        const data = {
          start_date: this.form.startDate || null,
          end_date: this.form.endDate || null,
          status: this.form.status,
          cert_status: this.form.certStatus,
          cert_remark: this.form.certRemark || null
        }
        const response = await updateRunnerReferrer(this.id, data)
        if (response && response.code === 0) {
          alert('修改成功')
          this.closeModal()
          this.loadDetail()
        } else {
          alert(response?.message || '修改失败')
        }
      } catch (error) {
        alert('操作失败，请稍后重试')
      }
    },
    async handleDelete() {
      if (!confirm('确定要删除该跑手推广人吗？此操作不可恢复。')) return
      try {
        const response = await deleteRunnerReferrer(this.id)
        if (response && response.code === 0) {
          alert('删除成功')
          this.goBack()
        } else {
          alert(response?.message || '删除失败')
        }
      } catch (error) {
        alert('删除失败，请稍后重试')
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return dateStr
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    walletStatusText(status) {
      const map = { 0: '正常', 1: '冻结', 2: '禁用', 3: '风控锁定' }
      return map[status] !== undefined ? map[status] : '未知'
    },
    walletStatusClass(status) {
      const map = { 0: 'normal', 1: 'frozen', 2: 'disabled', 3: 'risk' }
      return map[status] !== undefined ? map[status] : ''
    },
    txTypeText(type) {
      const map = {
        1: '充值',
        2: '消费',
        3: '提现',
        4: '退款',
        5: '冻结',
        6: '解冻',
        7: '服务费'
      }
      return map[type] !== undefined ? map[type] : '其他'
    },
    txStatusText(status) {
      const map = { '-1': '失败', 0: '处理中', 1: '成功' }
      return map[String(status)] !== undefined ? map[String(status)] : '未知'
    },
    certStatusText(status) {
      const map = {
        0: '待邀约',
        1: '已邀约待面谈',
        2: '已面谈待核验原件',
        3: '已核验证件原件待提交照片',
        4: '已提交证件照片待核验照片',
        5: '已核验证件照片待签约',
        6: '已签约',
        7: '已拒绝'
      }
      return map[status] !== undefined ? map[status] : '未知'
    },
    certStatusClass(status) {
      if (status === 6) return 'enabled'
      if (status === 7) return 'disabled'
      return 'pending'
    }
  }
}
</script>

<style scoped>
.referrer-detail-page {
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.page-title {
  margin: 0;
  font-size: 18px;
  color: #333;
  flex: 1;
}

.back-btn {
  padding: 8px 16px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.back-btn:hover {
  background: #eaeaea;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #fff;
}

.action-btn.edit {
  background: #f39c12;
}

.action-btn.delete-btn {
  background: #e74c3c;
}

.action-btn.add-runner-btn {
  background: #3498db;
  margin-left: auto;
}

.loading-state {
  padding: 40px;
  text-align: center;
  color: #999;
  font-size: 14px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.count-badge {
  display: inline-block;
  min-width: 20px;
  padding: 2px 8px;
  background: #e8f4fd;
  color: #3498db;
  border-radius: 20px;
  font-size: 12px;
  text-align: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 40px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.info-label {
  width: 90px;
  color: #999;
  flex-shrink: 0;
}

.info-value {
  color: #333;
  word-break: break-all;
}

.status-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.status-tag.enabled {
  background: #e8f5e9;
  color: #2ecc71;
}

.status-tag.disabled {
  background: #ffebee;
  color: #e74c3c;
}

.status-tag.pending {
  background: #e8f4fd;
  color: #3498db;
}

.table-container {
  overflow: hidden;
}

.runner-table {
  width: 100%;
  border-collapse: collapse;
}

.runner-table th,
.runner-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.runner-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.empty-row {
  text-align: center;
  color: #999;
  padding: 30px !important;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 450px;
  max-width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.modal-form {
  padding: 20px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
}

.form-input,
.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3498db;
}

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
}

.form-textarea:focus {
  outline: none;
  border-color: #3498db;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 10px 20px;
  background: #f5f5f5;
  color: #666;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.btn-submit {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.info-value.amount {
  font-weight: 600;
  color: #e67e22;
}

.wallet-status-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.wallet-status-tag.normal {
  background: #e8f5e9;
  color: #2ecc71;
}

.wallet-status-tag.frozen {
  background: #fff3e0;
  color: #f39c12;
}

.wallet-status-tag.disabled {
  background: #ffebee;
  color: #e74c3c;
}

.wallet-status-tag.risk {
  background: #f3e5f5;
  color: #9b59b6;
}

.tx-filter-bar {
  margin-bottom: 16px;
}

.filter-select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.tx-table {
  width: 100%;
  border-collapse: collapse;
}

.tx-table th,
.tx-table td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.tx-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.amount.plus {
  color: #2ecc71;
}

.amount.minus {
  color: #e74c3c;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}
</style>
